import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Info, Tag, RefreshCw } from "lucide-react";
import { getSalesOrders } from "../../api/salesService";
import { salesTypeService } from "../../api/masterService";

const formatCurrency = (val) => {
  const num = Number(val || 0);
  return `Rp ${num.toLocaleString("id-ID")}`;
};

const formatDateTime = (iso) => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getNextDayDate = (dateStr) => {
  if (!dateStr) return dateStr;
  const parts = String(dateStr).split("-");
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const d = new Date(year, month, day + 1);
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const dt = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${dt}`;
  }
  return dateStr;
};

const isItemMatchingSalesType = (item, targetTypeName) => {
  const target = String(targetTypeName || "").trim().toLowerCase();
  const sellerType = String(item?.seller_type || item?.sellerType || "").trim().toLowerCase();
  const orderTypeName = String(item?.order_type?.name || item?.OrderType?.name || "").trim().toLowerCase();

  if (sellerType === target || orderTypeName === target) return true;
  if (sellerType && target && (sellerType.includes(target) || target.includes(sellerType))) return true;
  if (orderTypeName && target && (orderTypeName.includes(target) || target.includes(orderTypeName))) return true;

  // Standard/Default type matching: "pelanggan umum", "reguler", "internal", "normal", "walk-in"
  const isDefaultTarget =
    target === "pelanggan umum" ||
    target === "reguler" ||
    target === "internal" ||
    target === "normal" ||
    target === "walk-in" ||
    target === "umum" ||
    target === "-";

  const isDefaultItem =
    !sellerType ||
    sellerType === "internal" ||
    sellerType === "reguler" ||
    sellerType === "pelanggan umum" ||
    sellerType === "normal" ||
    sellerType === "walk-in" ||
    orderTypeName === "pelanggan umum" ||
    orderTypeName === "reguler" ||
    orderTypeName === "internal";

  if (isDefaultTarget && isDefaultItem) return true;

  return false;
};

const getEffectiveDiscountAndMarkup = (ord, masterSalesTypes = []) => {
  const grand = Number(ord.grand_total ?? ord.grandTotal ?? ord.total_per_nota ?? 0);
  let disc = Number(ord.discount_total ?? ord.discountTotal ?? ord.discount ?? ord.discount_amount ?? 0);
  let add = Number(ord.additional_costs ?? ord.additionalCosts ?? ord.additional_cost ?? ord.other_cost ?? 0);

  // Sum item total prices if available for true pre-discount subtotal
  let sub = 0;
  if (Array.isArray(ord?.items) && ord.items.length > 0) {
    sub = ord.items.reduce((s, i) => {
      const itemPrice = Number(i.total_price ?? ((i.quantity || 1) * (i.unit_price || 0)) ?? 0);
      return s + itemPrice;
    }, 0);
  }

  if (sub <= 0) {
    sub = Number(ord.subtotal ?? ord.sub_total ?? ord.subTotal ?? 0);
  }

  const sellerName = String(
    ord.seller_type || ord.sellerType || ord.order_type?.name || ""
  ).trim().toLowerCase();

  const matchedType = masterSalesTypes.find(
    (t) => String(t.name || "").trim().toLowerCase() === sellerName
  );

  // Fallback subtotal calculation if subtotal is missing or 0
  if (sub <= 0 && grand > 0) {
    if (matchedType) {
      const nilai = Number(matchedType.nilai || 0);
      const satuan = String(matchedType.satuan || "").trim();
      if (matchedType.jenis === "Diskon" && nilai > 0) {
        if (satuan === "%" && nilai < 100) {
          sub = Math.round((grand * 100) / (100 - nilai));
          if (disc <= 0) disc = sub - grand;
        } else if (satuan !== "%") {
          sub = grand + nilai;
          if (disc <= 0) disc = nilai;
        }
      } else if (matchedType.jenis === "Markup" && nilai > 0) {
        if (satuan === "%") {
          sub = Math.round((grand * 100) / (100 + nilai));
          if (add <= 0) add = grand - sub;
        } else if (satuan !== "%") {
          sub = Math.max(0, grand - nilai);
          if (add <= 0) add = nilai;
        }
      }
    }
    if (sub <= 0) {
      sub = grand + disc - add;
    }
  }

  // MATHEMATICAL INVARIANT RECOVERY:
  // If item subtotal > grand_total + add and disc is still 0, the difference MUST be discount!
  if (disc <= 0 && sub > 0 && sub + add > grand) {
    disc = sub + add - grand;
  }

  // If grand_total > sub - disc and add is still 0, the difference MUST be markup!
  if (add <= 0 && grand > 0 && grand > sub - disc) {
    add = grand - (sub - disc);
  }

  // Check matched sale type rules for percentage/fixed type fallback if still 0
  if (disc <= 0 && matchedType && matchedType.jenis === "Diskon" && matchedType.nilai > 0) {
    const nilai = Number(matchedType.nilai);
    const satuan = String(matchedType.satuan || "").trim();
    if (satuan === "%" && sub > 0) {
      disc = Math.round((sub * nilai) / 100);
    } else if (satuan !== "%") {
      disc = nilai;
    }
  }

  if (add <= 0 && matchedType && matchedType.jenis === "Markup" && matchedType.nilai > 0) {
    const nilai = Number(matchedType.nilai);
    const satuan = String(matchedType.satuan || "").trim();
    if (satuan === "%" && sub > 0) {
      add = Math.round((sub * nilai) / 100);
    } else if (satuan !== "%") {
      add = nilai;
    }
  }

  return { sub, disc, add, grand };
};

function SalesTypeExpandableRow({
  row,
  idx,
  startDate,
  endDate,
  masterSalesTypes = [],
  onSelectTransaction,
}) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);

  const salesTypeName = row.sales_type || row.name || "-";

  useEffect(() => {
    if (expanded && !hasLoaded) {
      setLoading(true);
      setError(null);

      const effectiveEndDate = getNextDayDate(endDate);

      // Fetch SEMUA halaman (limit maksimal 100 per halaman) supaya data
      // di periode lama tidak terpotong oleh page 1 saja.
      const fetchAllPages = async () => {
        const PAGE_LIMIT = 100;
        let page = 1;
        let allItems = [];
        try {
          while (page <= 50) {
            const res = await getSalesOrders({
              start_date: startDate,
              end_date: effectiveEndDate,
              status: "DONE",
              page,
              limit: PAGE_LIMIT,
            });
            const list = res?.data ?? res ?? [];
            const items = Array.isArray(list) ? list : list?.items || list?.data || [];
            const meta = res?.meta ?? list?.meta ?? null;

            if (Array.isArray(items)) allItems = allItems.concat(items);

            const total = Number(meta?.total ?? 0);
            if (total > 0 && allItems.length >= total) break;
            if (!items || items.length === 0) break;
            page += 1;
          }

          const filtered = allItems.filter((item) =>
            isItemMatchingSalesType(item, salesTypeName)
          );

          setOrders(filtered);
          setHasLoaded(true);
        } catch (err) {
          setError(err.message || "Gagal memuat transaksi. Silakan periksa koneksi atau klik Refresh Data.");
        } finally {
          setLoading(false);
        }
      };

      fetchAllPages();
    }
  }, [expanded, hasLoaded, salesTypeName, startDate, endDate]);

  // Total keseluruhan (selalu dari semua data, bukan visible)
  const totals = orders.reduce(
    (acc, ord) => {
      const { sub, disc, add, grand } = getEffectiveDiscountAndMarkup(ord, masterSalesTypes);
      acc.subtotal += sub;
      acc.discount += disc;
      acc.additional += add;
      acc.grandTotal += grand;
      return acc;
    },
    { subtotal: 0, discount: 0, additional: 0, grandTotal: 0 }
  );

  const PAGE_SIZE = 15;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const visibleOrders = orders.slice(0, visibleCount);
  const hasMore = visibleCount < orders.length;
  const remaining = orders.length - visibleCount;

  return (
    <>
      <tr
        onClick={() => setExpanded((prev) => !prev)}
        className={`cursor-pointer transition-colors ${
          expanded ? "bg-emerald-50/70 font-medium" : "hover:bg-slate-50/80"
        }`}>
        <td className="px-4 py-3.5 whitespace-nowrap text-slate-700 font-semibold">
          {idx + 1}
        </td>
        <td className="px-4 py-3.5 whitespace-nowrap font-bold text-slate-900">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-emerald-100 text-emerald-800 flex items-center justify-center flex-shrink-0">
              {expanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </span>
            <span>{salesTypeName}</span>
          </div>
        </td>
        <td className="px-4 py-3.5 whitespace-nowrap text-slate-700 font-medium">
          {row.transaction_count || 0} Transaksi
        </td>
        <td className="px-4 py-3.5 whitespace-nowrap text-slate-600">
          {row.transaction_ratio ? `${Number(row.transaction_ratio).toFixed(1)}%` : "0%"}
        </td>
        <td className="px-4 py-3.5 whitespace-nowrap font-bold text-emerald-700">
          {formatCurrency(row.total_sales || 0)}
        </td>
      </tr>

      {/* Expanded Lipatan Panel */}
      {expanded && (
        <tr>
          <td colSpan={5} className="p-0 bg-slate-50/60">
            <div className="p-4 border-y border-emerald-200 bg-gradient-to-b from-emerald-50/40 to-slate-50/80 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-950">
                  <Tag className="w-4 h-4 text-emerald-600" />
                  <h4 className="font-bold text-sm">
                    Daftar Transaksi Tipe Penjualan: <span className="text-emerald-700 font-extrabold">{salesTypeName}</span>
                  </h4>
                  <span className="text-xs text-slate-500 font-normal">
                    ({startDate || "Awal"} s/d {endDate || "Sekarang"})
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setHasLoaded(false)}
                  className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-700 font-medium">
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh Data
                </button>
              </div>

              {loading ? (
                <div className="py-8 text-center text-xs text-slate-500">
                  Memuat rincian transaksi {salesTypeName}...
                </div>
              ) : error ? (
                <div className="py-8 text-center text-xs text-rose-600">{error}</div>
              ) : orders.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-400">
                  Tidak ada transaksi tercatat untuk tipe penjualan "{salesTypeName}" pada periode ini.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200 bg-white shadow-sm">
                  <table className="min-w-full text-xs">
                    <thead className="bg-slate-100/90 border-b border-slate-200 font-semibold text-slate-600 uppercase tracking-wider text-left">
                      <tr>
                        <th className="px-3.5 py-2.5">No. Transaksi</th>
                        <th className="px-3.5 py-2.5">Waktu</th>
                        <th className="px-3.5 py-2.5">Pelanggan</th>
                        <th className="px-3.5 py-2.5 text-right">Subtotal</th>
                        <th className="px-3.5 py-2.5 text-right">Diskon Tipe</th>
                        <th className="px-3.5 py-2.5 text-right">Biaya/Markup Tipe</th>
                        <th className="px-3.5 py-2.5 text-right">Grand Total</th>
                        <th className="px-3.5 py-2.5 text-center">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {visibleOrders.map((ord, oIdx) => {
                        const { sub, disc, add, grand } = getEffectiveDiscountAndMarkup(ord, masterSalesTypes);

                        return (
                          <tr key={ord.id || oIdx} className="hover:bg-emerald-50/50 transition-colors">
                            <td className="px-3.5 py-2.5 font-semibold text-slate-900 whitespace-nowrap">
                              {ord.transaction_number || "-"}
                            </td>
                            <td className="px-3.5 py-2.5 text-slate-500 whitespace-nowrap">
                              {formatDateTime(ord.created_at || ord.order_date)}
                            </td>
                            <td className="px-3.5 py-2.5 font-medium text-slate-700 whitespace-nowrap">
                              {ord.customer_name || "Pelanggan Umum"}
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-medium text-slate-700 whitespace-nowrap">
                              {formatCurrency(sub)}
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-semibold text-rose-600 whitespace-nowrap">
                              {disc > 0 ? `- ${formatCurrency(disc)}` : "-"}
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-semibold text-blue-600 whitespace-nowrap">
                              {add > 0 ? `+ ${formatCurrency(add)}` : "-"}
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-bold text-slate-900 whitespace-nowrap">
                              {formatCurrency(grand)}
                            </td>
                            <td className="px-3.5 py-2.5 text-center whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() =>
                                  onSelectTransaction({
                                    ...ord,
                                    showSaleTypeDiscount: true,
                                  })
                                }
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold transition-colors ring-1 ring-emerald-200">
                                <Info className="w-3.5 h-3.5" />
                                Detail
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Gradient blur + Lihat Selengkapnya */}
                  {hasMore && (
                    <div className="relative">
                      {/* Gradient overlay: putih transparan bawah → putih solid atas */}
                      <div
                        className="absolute inset-x-0 bottom-full h-16 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(to top, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.7) 50%, transparent 100%)",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-white border-t border-emerald-100 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold transition-colors group">
                        <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                        Lihat Selengkapnya
                        <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full text-[10px]">
                          +{remaining} lagi
                        </span>
                      </button>
                    </div>
                  )}

                  {/* REKAP TOTAL — selalu total keseluruhan semua data */}
                  <div className="border-t-2 border-emerald-300 bg-emerald-100/60">
                    <table className="min-w-full text-xs">
                      <tfoot className="font-bold text-slate-900">
                        <tr>
                          <td colSpan={3} className="px-3.5 py-3 text-emerald-950 uppercase tracking-wider text-[11px]">
                            REKAP TOTAL ({salesTypeName}) — {orders.length} Transaksi
                          </td>
                          <td className="px-3.5 py-3 text-right text-slate-900 font-bold whitespace-nowrap">
                            {formatCurrency(totals.subtotal)}
                          </td>
                          <td className="px-3.5 py-3 text-right text-rose-600 font-extrabold whitespace-nowrap">
                            {totals.discount > 0 ? `- ${formatCurrency(totals.discount)}` : "-"}
                          </td>
                          <td className="px-3.5 py-3 text-right text-blue-600 font-extrabold whitespace-nowrap">
                            {totals.additional > 0 ? `+ ${formatCurrency(totals.additional)}` : "-"}
                          </td>
                          <td className="px-3.5 py-3 text-right text-emerald-900 font-extrabold text-sm whitespace-nowrap">
                            {formatCurrency(totals.grandTotal)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SalesTypeRecapTable({
  rows = [],
  loading = false,
  error = null,
  startDate,
  endDate,
  onSelectTransaction,
}) {
  const [masterSalesTypes, setMasterSalesTypes] = useState([]);

  useEffect(() => {
    let mounted = true;
    salesTypeService
      .list({ limit: 200 })
      .then((res) => {
        const list = res?.data ?? res ?? [];
        if (mounted) {
          setMasterSalesTypes(Array.isArray(list) ? list : list.data ?? []);
        }
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm divide-y divide-slate-200">
        <thead className="bg-slate-50/90 border-b border-slate-200">
          <tr>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
              No
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
              Tipe Penjualan
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
              Jumlah Transaksi
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
              Rasio (%)
            </th>
            <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
              Total Sales
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {loading && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                Memuat data rekap tipe penjualan...
              </td>
            </tr>
          )}

          {!loading && error && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-rose-600">
                {error}
              </td>
            </tr>
          )}

          {!loading && !error && rows.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                Tidak ada data rekap tipe penjualan yang ditemukan.
              </td>
            </tr>
          )}

          {!loading &&
            !error &&
            rows.map((row, idx) => (
              <SalesTypeExpandableRow
                key={row.sales_type || idx}
                row={row}
                idx={idx}
                startDate={startDate}
                endDate={endDate}
                masterSalesTypes={masterSalesTypes}
                onSelectTransaction={onSelectTransaction}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
}
